import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

interface ModalProps {
  title: string; onClose: () => void; children: React.ReactNode; width?: number;
  footer?: React.ReactNode; historyMode?: 'push' | 'none'; closeDisabled?: boolean;
}
const Overlay = styled.div`
  position: fixed; inset: 0; display: flex; align-items: center; justify-content: center;
  padding: 16px; background: rgba(16, 20, 28, 0.36); z-index: 1000;
  -webkit-backdrop-filter: blur(4px); backdrop-filter: blur(4px);
`;
const Content = styled.div<{ $width?: number }>`
  width: ${({ $width }) => $width || 520}px; max-width: calc(100vw - 32px); max-height: calc(100dvh - 32px);
  display: flex; flex-direction: column; min-height: 0; border-radius: 8px;
  background: ${({ theme }) => theme.color.primary_100}; color: ${({ theme }) => theme.color.text_color_100};
  border: 1px solid ${({ theme }) => theme.color.primary_300}; box-shadow: 0 20px 64px #00000024;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 14px 20px; flex-shrink: 0; min-height: 56px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};
  h2 { font-size: 15px; line-height: 24px; font-weight: 600; margin: 0; }
`;
const Close = styled.button`
  display: grid; place-items: center; width: 28px; height: 28px; padding: 0; border: 0; border-radius: 5px;
  background: transparent; color: ${({ theme }) => theme.color.text_color_300}; cursor: pointer; font-size: 22px;
  &:hover { background: ${({ theme }) => theme.color.primary_50}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
`;
const Body = styled.div`padding: 20px; overflow-y: auto; min-height: 0; flex: 1;`;
const Footer = styled.div`
  display: flex; align-items: center; justify-content: flex-end; gap: 8px; min-height: 64px; padding: 12px 20px;
  flex-shrink: 0; border-top: 1px solid ${({ theme }) => theme.color.primary_300};
`;
// One stack for all portal dialogs, so only the foreground dialog owns focus and Escape.
const dialogs: HTMLElement[] = [];
const activeHistory = new Set<string>();
let firstInvoker: HTMLElement | null = null;
const focusSelector = 'button:not([disabled]), a[href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), audio[controls], video[controls], [tabindex]:not([tabindex="-1"])';
const focusable = (dialog: HTMLElement) => Array.from(dialog.querySelectorAll<HTMLElement>(focusSelector)).filter(el => !el.closest('[aria-hidden="true"], [hidden], [inert]'));

const syncStack = () => dialogs.forEach((dialog, index) => {
  const active = index === dialogs.length - 1;
  dialog.toggleAttribute('inert', !active);
  dialog.setAttribute('aria-hidden', String(!active));
  if (dialog.parentElement) dialog.parentElement.style.zIndex = String(1000 + index * 100);
});

export default function Modal({ title, onClose, children, width, footer, historyMode = 'push', closeDisabled = false }: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = closeDisabled ? () => undefined : onClose;
  useEffect(() => {
    const dialog = dialogRef.current!;
    const invoking = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (!dialogs.length) firstInvoker = invoking;
    dialogs.push(dialog);
    syncStack();
    const isTop = () => dialogs[dialogs.length - 1] === dialog;
    if (historyMode === 'push') {
      activeHistory.add(titleId);
      window.history.pushState({ ...window.history.state, isModalOpen: true, callisModalId: titleId, callisModalIds: [...(window.history.state?.callisModalIds || []), titleId] }, '');
    }
    const onPop = () => {
      if (!isTop() || window.history.state?.callisModalId === titleId) return;
      onCloseRef.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (!isTop()) return;
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current(); return; }
      if (event.key !== 'Tab') return;
      // Native media has its own shadow-DOM tab stops; the end focus guard catches exit.
      if (document.activeElement?.matches('audio[controls],video[controls]')) return;
      const elements = focusable(dialog);
      const first = elements[0], last = elements[elements.length - 1];
      if (!first) { event.preventDefault(); dialog.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    if (historyMode === 'push') window.addEventListener('popstate', onPop);
    (dialog.querySelector<HTMLElement>('input:not([type="hidden"]):not([disabled]),select:not([disabled]),textarea:not([disabled])') || closeRef.current)?.focus();
    return () => {
      const wasTop = isTop();
      dialogs.splice(dialogs.indexOf(dialog), 1);
      syncStack();
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onPop);
      if (historyMode === 'push') {
        activeHistory.delete(titleId);
        const historyIds: string[] = window.history.state?.callisModalIds || [];
        let removed = 0;
        for (let i = historyIds.length - 1; i >= 0 && !activeHistory.has(historyIds[i]); i -= 1) removed += 1;
        if (removed) window.history.go(-removed);
      }
      if (wasTop) {
        if (invoking?.isConnected) invoking.focus();
        else if (!dialogs.length && firstInvoker?.isConnected) firstInvoker.focus();
      }
      if (!dialogs.length) firstInvoker = null;
    };
  }, [historyMode, titleId]);
  const focusEdge = (last: boolean) => {
    const dialog = dialogRef.current;
    if (!dialog || dialogs[dialogs.length - 1] !== dialog) return;
    const elements = focusable(dialog);
    (last ? elements[elements.length - 1] : elements[0])?.focus();
  };
  return createPortal(<Overlay onClick={() => { if (dialogs[dialogs.length - 1] === dialogRef.current) onCloseRef.current(); }}>
    <Content ref={dialogRef} $width={width} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onClick={event => event.stopPropagation()}>
      <span data-focus-guard tabIndex={0} aria-hidden="true" style={{ position: 'fixed', width: 1, height: 1, opacity: 0 }} onFocus={() => focusEdge(true)} />
      <Header><h2 id={titleId}>{title}</h2><Close type="button" ref={closeRef} aria-label="닫기" disabled={closeDisabled} onClick={onClose}>×</Close></Header>
      <Body>{children}</Body>{footer && <Footer>{footer}</Footer>}
      <span data-focus-guard tabIndex={0} aria-hidden="true" style={{ position: 'fixed', width: 1, height: 1, opacity: 0 }} onFocus={() => focusEdge(false)} />
    </Content>
  </Overlay>, document.body);
}
